import { api } from "api/apiService";

/**
 * Завантажує список SKU пасток з урахуванням параметрів фільтрації.
 * @param {object} [filters={}] - Об'єкт з параметрами для фільтрації.
 * @returns {Promise<object>} Проміс, що повертає об'єкт з даними про SKU пасток.
 */
export const fetchTrapSkus = (filters = {}) => {
  // Копіюємо лише ті фільтри, які мають реальне значення
  const cleanFilters = {};
  for (const key in filters) {
    const value = filters[key];
    if (value !== null && value !== undefined && value !== "") {
      cleanFilters[key] = value;
    }
  }

  return api.get("/trap-sku", cleanFilters);
};

/**
 * Завантажує магазини, що мають ловушки, з усіх платформ (Prom + Horoshop).
 * Окремий ендпоінт від /shops: той віддає лише Prom і містить токени.
 * @returns {Promise<Array>} Проміс з масивом { platform, shop_id, name, trap_sku }.
 */
export const fetchTrapShops = () => {
  return api.get("/trap-sku/shops");
};

/**
 * Оновлює ціну для конкретного SKU пастки.
 * @param {string} sku - Артикул (SKU), для якого оновлюється ціна.
 * @param {number} price - Нова ціна.
 * @returns {Promise<object>} Проміс, що повертає відповідь від сервера.
 */
export const updateTrapSkuPrice = (sku, price) => {
  const url = `/trap-sku/${encodeURIComponent(sku)}/price`;

  return api.put(url, { price });
};

/**
 * Оновлює категорію для конкретного SKU пастки.
 * @param {string} sku - Артикул (SKU), для якого оновлюється категорія.
 * @param {number|string} categoryId - ID нової категорії.
 * @returns {Promise<object>} Проміс, що повертає відповідь від сервера.
 */
export const updateTrapSkuCategory = (sku, categoryId) => {
  const url = `/trap-sku/${encodeURIComponent(sku)}/category`;

  return api.put(url, { category_id: categoryId });
};

/**
 * Оновлює статус наявності для конкретного SKU пастки.
 * @param {string} sku - Артикул (SKU), для якого оновлюється наявність.
 * @param {boolean} presence - Новий статус наявності (true/false).
 * @returns {Promise<object>} Проміс, що повертає відповідь від сервера.
 */
export const updateTrapSkuPresence = (sku, presence) => {
  const url = `/trap-sku/${encodeURIComponent(sku)}/presence`;

  return api.put(url, { presence });
};

/**
 * Масово оновлює статус наявності для списку SKU.
 * @param {string[]} skus - Масив артикулів (SKU) для оновлення.
 * @param {string} presence - Новий статус наявності ("available", "not_available", "order").
 * @returns {Promise<object>} Проміс, що повертає відповідь від сервера про кількість оновлених записів.
 */
export const massUpdateTrapSkuPresence = (skus, presence) => {
  const data = {
    skus: skus,
    presence: presence,
  };

  return api.put("/trap-sku/bulk/presence", data);
};

/**
 * Масово встановлює кастомні ціни (націнку) для списку SKU.
 * @param {string[]} skus - Масив артикулів (SKU) для оновлення.
 * @param {string} markupType - Тип націнки ("percentage" або "fixed").
 * @param {number} markupValue - Значення націнки.
 * @returns {Promise<object>} Проміс, що повертає відповідь від сервера.
 */
export const massUpdateCustomPrices = (skus, markupType, markupValue) => {
  const data = {
    skus,
    markupType,
    markupValue,
  };

  return api.put("/trap-sku/bulk/custom-prices", data);
};

/**
 * Масово скидає кастомні ціни для списку SKU.
 * @param {string[]} skus - Масив артикулів (SKU) для скидання.
 * @returns {Promise<object>} Проміс, що повертає відповідь від сервера.
 */
export const massResetCustomPrices = (skus) => {
  return api.post("/trap-sku/bulk/custom-prices/reset", { skus });
};

/**
 * Встановлює кастомну ціну для конкретного SKU пастки.
 * @param {string} sku - Артикул (SKU) пастки.
 * @param {number} price - Нова кастомна ціна.
 * @returns {Promise<object>} Проміс, що повертає відповідь від сервера.
 */
export const updateTrapCustomPrice = (sku, price) => {
  const url = `/trap-sku/${encodeURIComponent(sku)}/custom-price`;
  return api.put(url, { price });
};

/**
 * Видаляє кастомну ціну для конкретного SKU пастки.
 * @param {string} sku - Артикул (SKU) пастки.
 * @returns {Promise<object>} Проміс, що повертає відповідь від сервера.
 */
export const removeTrapCustomPrice = (sku) => {
  const url = `/trap-sku/${encodeURIComponent(sku)}/custom-price`;
  return api.delete(url);
};

/**
 * Завантажує список товарів-переводів, прив'язаних до категорії ловушки.
 * @param {number|string} categoryId - ID категорії ловушки.
 * @returns {Promise<Array>} Проміс з масивом товарів.
 */
export const fetchTrapProducts = (categoryId) => {
  const url = `/trap-sku/${encodeURIComponent(categoryId)}/products`;
  return api.get(url);
};

/**
 * Перемикає авто-режим наявності ловушки.
 * @param {string} sku - Артикул ловушки.
 * @param {boolean} auto - true для ввімкнення авто-режиму, false для вимкнення.
 * @returns {Promise<object>} Проміс з відповіддю сервера.
 */
export const toggleTrapAuto = (sku, auto) => {
  const url = `/trap-sku/${encodeURIComponent(sku)}/auto`;
  return api.put(url, { auto });
};
